import { useRecoilValue, useSetRecoilState } from 'recoil';
import { getFieldIdsOfLayoutField } from 'routes/FormTemplateEditor/FormEditor/utils';
import { getId } from 'utils';
import {
  FieldTemplate,
  FieldType,
  OptionSourceType,
  ConditionGroup,
  Condition,
  ConditionType,
} from 'api/models/FieldTemplate';
import { LayoutItem } from 'api/models';
import {
  ToastSeverity,
  fieldTemplateAliases,
  fieldTemplatesState,
  toastState,
} from 'state';
import { useParams } from 'react-router-dom';
import { GeneralIds } from 'types/types';
import { getFieldAlias } from 'api';
import { clipboardAtom } from 'state/clipboardState';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export function useCopyLayoutItem() {
  const fieldTemplates = useRecoilValue(fieldTemplatesState);
  const { projectId } = useParams<GeneralIds>();
  const usedAliases = useRecoilValue(fieldTemplateAliases);
  const setClipboard = useSetRecoilState(clipboardAtom);
  const [loading, setLoading] = useState(false);
  const setToast = useSetRecoilState(toastState);
  const { t } = useTranslation();

  async function setClipboardCallback(layoutItem: LayoutItem) {
    setLoading(true);
    const fieldTemplateIds = getFieldIdsOfLayoutField(layoutItem);
    const updatedLayoutItem = recursivelyUpdateIds(layoutItem);
    let layoutItemString = JSON.stringify(updatedLayoutItem);
    const newAliases = [...usedAliases];
    const aliasMapping: Record<string, string> = {};

    const copiedFields = [];
    for (const fieldTemplateId of fieldTemplateIds) {
      const fieldTemplate = fieldTemplates.find(
        ({ id }) => id === fieldTemplateId,
      );
      if (!fieldTemplate) {
        continue;
      }

      const newFieldTemplateId = getId();
      const foundLayoutItemId = recursivelyFindLayoutItemIdByFieldTemplateId(
        fieldTemplateId,
        // @ts-ignore
        updatedLayoutItem,
      );

      layoutItemString = layoutItemString.replaceAll(
        fieldTemplateId,
        newFieldTemplateId,
      );

      const newAlias = await getFieldAlias({
        title: fieldTemplate.alias,
        usedAliases: newAliases,
        projectId,
      });

      newAliases.push(newAlias);

      // Map old alias to new alias for updating isVisible references
      aliasMapping[fieldTemplate.alias] = newAlias;

      const copiedField = {
        id: newFieldTemplateId,
        fieldType: fieldTemplate.fieldType,
        fieldAttributes: fieldTemplate.fieldAttributes,
        formFieldTemplateId: foundLayoutItemId,
        alias: newAlias,
        isVisible: updateIsVisibleAliases(
          fieldTemplate.isVisible,
          aliasMapping,
        ),
        isRequired: fieldTemplate.isRequired,
        version: 0,
      };

      if (fieldTemplate.fieldType !== FieldType.Select) {
        copiedFields.push(copiedField);
        continue;
      }

      // if it's a select and it does not have it's own options we don't need to copy the options
      if (
        fieldTemplate.fieldAttributes.optionsSourceType ===
          OptionSourceType.FieldTemplate ||
        fieldTemplate.fieldAttributes.optionsSourceType ===
          OptionSourceType.CodeLibrary
      ) {
        copiedFields.push(copiedField);
        continue;
      }

      // if it's a select and it has it's own options we need to copy the options
      copiedFields.push({
        ...copiedField,
        fieldAttributes: {
          ...fieldTemplate.fieldAttributes,
          options: null,
          optionsSourceType: OptionSourceType.FieldTemplate,
          optionsSourceFieldTemplateId: fieldTemplate.id,
          optionsSourceFieldAlias: fieldTemplate.alias,
          optionsSourceCodeLibraryId: fieldTemplate.alias,
        },
      });
    }

    // Update isVisible references in all copied fields after all aliases are generated
    const updatedCopiedFields = copiedFields.map(field => ({
      ...field,
      isVisible: updateIsVisibleAliases(field.isVisible, aliasMapping),
    }));

    // const copies = copiedFields.filter(Boolean);
    const newLayoutItem = JSON.parse(layoutItemString) as LayoutItem;

    setLoading(false);

    setClipboard({
      layoutItem: newLayoutItem,
      fieldTemplates: updatedCopiedFields as FieldTemplate[],
    });

    setToast({
      message: t('toastMessages.fieldCopiedSuccessfully'),
      severity: ToastSeverity.success,
      isOpen: true,
    });
  }

  return { loading, setClipboard: setClipboardCallback };
}

function recursivelyUpdateIds(
  layoutItem: LayoutItem,
): LayoutItem | LayoutItem[] {
  const newChildren = layoutItem.children?.map(child =>
    recursivelyUpdateIds(child),
  );
  return {
    ...layoutItem,
    id: getId(),
    // @ts-ignore
    children: newChildren,
  };
}

function recursivelyFindLayoutItemIdByFieldTemplateId(
  fieldTemplateId: string,
  layoutItem: LayoutItem,
): string | undefined {
  if (layoutItem.fieldTemplateId === fieldTemplateId) {
    return layoutItem.id;
  }
  if (layoutItem.children) {
    for (const child of layoutItem.children) {
      const found = recursivelyFindLayoutItemIdByFieldTemplateId(
        fieldTemplateId,
        child,
      );
      if (found) {
        return found;
      }
    }
  }
  return undefined;
}

/**
 * Updates isVisible condition aliases to use new aliases for copied field templates
 */
function updateIsVisibleAliases(
  isVisible: ConditionGroup | undefined,
  aliasMapping: Record<string, string>,
): ConditionGroup | undefined {
  if (!isVisible) {
    return isVisible;
  }

  return updateConditionGroupAliases(isVisible, aliasMapping);
}

function updateConditionGroupAliases(
  conditionGroup: ConditionGroup,
  aliasMapping: Record<string, string>,
): ConditionGroup {
  const updatedConditions = conditionGroup.conditions.map(condition => {
    if (condition.type === ConditionType.conditionGroup) {
      return updateConditionGroupAliases(
        condition as ConditionGroup,
        aliasMapping,
      );
    } else {
      return updateConditionAliases(condition as Condition, aliasMapping);
    }
  });

  return {
    ...conditionGroup,
    conditions: updatedConditions as ConditionGroup[] | Condition[],
  };
}

function updateConditionAliases(
  condition: Condition,
  aliasMapping: Record<string, string>,
): Condition {
  const updatedCondition = { ...condition };

  if (condition.reference?.alias && aliasMapping[condition.reference.alias]) {
    updatedCondition.reference = {
      ...condition.reference,
      alias: aliasMapping[condition.reference.alias],
    };
  }

  return updatedCondition;
}
